import {
  Badge,
  Button,
  ButtonList,
  Column,
  DropdownButton,
  FlexBox,
  Page,
  SearchInput,
  Spinner,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useToggle } from 'react-use';

import { PresenceFilter } from 'hocs/presence/withPresence';
import { useBreakpointValue } from 'utils/mediaQuery';

import styles from './teamHeader.module.scss';

type TeamRightHeaderProps = {
  filter: PresenceFilter;
  isAssignedOnly: boolean;
  itemsAsyncStatus: AsyncStatus | null;
  total: number | null;
  searchTerm: string | undefined;
  onAssignedOnlyClicked: () => void;
  onSearchClose: () => void;
  onFilterClick: (key: PresenceFilter) => void;
  onSearch: (values: { team_search: string }) => void;
};

const filterToLabels: Record<PresenceFilter, string> = {
  all: 'All agents',
  online: 'Online only',
  away: 'Away only',
  offline: 'Offline only',
  favourite: 'Favorites only',
};

export const TeamHeader = ({
  filter,
  isAssignedOnly,
  itemsAsyncStatus,
  searchTerm,
  total,
  onAssignedOnlyClicked,
  onSearchClose,
  onFilterClick,
  onSearch,
}: TeamRightHeaderProps): JSX.Element => {
  const isSmallScreen = useBreakpointValue({ base: true, sm: false });
  const [isSearchOpen, toggleSearchOpen] = useToggle(false);

  const handleSearchSubmit = (value: string) => {
    onSearch({ team_search: value });
  };

  const handleSearchToggle = (isOpen: boolean) => {
    toggleSearchOpen(isOpen);

    if (!isOpen && searchTerm) {
      onSearchClose();
    }
  };

  const controls = (
    <div
      className={classnames(styles.controls, {
        [styles.controlsWithSearchOpen]: isSearchOpen,
      })}
    >
      <SearchInput
        initialSearch={searchTerm}
        onSubmit={handleSearchSubmit}
        onToggle={handleSearchToggle}
      />

      <Button
        variant={isAssignedOnly ? 'primary' : 'secondary'}
        onClick={onAssignedOnlyClicked}
      >
        Assigned agents only
      </Button>

      <DropdownButton
        buttonProps={{ variant: 'tertiary' }}
        onToggle={(isOpen) => isOpen && ampli.teamFilterMenuOpened()}
        dropdown={
          <ButtonList>
            {Object.keys(filterToLabels).map((key) => (
              <ButtonList.Item
                key={key}
                isSelected={filter === key}
                onClick={() => onFilterClick(key as PresenceFilter)}
              >
                {filterToLabels[key]}
              </ButtonList.Item>
            ))}
          </ButtonList>
        }
      >
        {filterToLabels[filter]}
      </DropdownButton>
    </div>
  );

  return (
    <>
      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            Team
            <Badge variant="secondary" size="medium">
              {itemsAsyncStatus === 'loading' ? (
                <Spinner size={16} />
              ) : (
                <span>{total?.toString() ?? '0'}</span>
              )}
            </Badge>
          </FlexBox>
        }
        controls={!isSmallScreen && controls}
      />

      {isSmallScreen && (
        <Column.Row noMinHeight borderBottom>
          {controls}
        </Column.Row>
      )}
    </>
  );
};
